import React, { type ReactNode } from 'react';
import Button from '@atlaskit/button/standard-button';
import MoreIcon from '@atlaskit/icon/core/migration/show-more-horizontal--more';
import { Box, Text, xcss } from '@atlaskit/primitives';
import {
	AdControlDropdownMenu,
	DismissalButton,
	ReasonsButton,
} from '@atlassian/ad-control-toolkit';
import { useIntl } from '@atlassian/jira-intl';
import {
	ContextualAnalyticsData,
	MODAL,
	MountEvent,
	fireScreenAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
	type AnalyticsEvent,
} from '@atlassian/jira-product-analytics-bridge';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import messages from './messages.tsx';

const DATA_PRIVACY_LINK = 'https://www.atlassian.com/legal/privacy-policy';
const QUICK_MENU_TYPE = 'loom-only';

type Props = {
	onDismiss: () => Promise<void>;
	menuLocation?: string;
	bannerLocation?: string;
};

// AdControlProvider is needed when importing this component to provide the context for the AdControlDropdownMenu
const AdControls = ({ onDismiss, menuLocation, bannerLocation }: Props) => {
	const { formatMessage } = useIntl();
	const isSiteAdmin = useIsSiteAdmin();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const eventAttributes = {
		bannerLocation,
		menuLocation,
		isSiteAdmin,
		quickMenuType: menuLocation === 'quick-add' ? QUICK_MENU_TYPE : undefined,
	};

	const buttonClicked = createAnalyticsEvent({
		action: 'clicked',
		actionSubject: 'button',
	});

	const linkClicked = createAnalyticsEvent({
		action: 'clicked',
		actionSubject: 'link',
	});

	return (
		<AdControlDropdownMenu
			itemId="loom-videos-banner-ad-controls"
			trigger={({ triggerRef, ...props }) => (
				// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
				<Box
					paddingInline="space.050"
					onClick={() => {
						fireUIAnalytics(buttonClicked, 'adControlsButton', eventAttributes);
					}}
				>
					<Button
						{...props}
						appearance="subtle"
						spacing="compact"
						iconBefore={
							<MoreIcon
								label={formatMessage(messages.loomAdControlsTrigger)}
								LEGACY_size="medium"
								spacing="spacious"
							/>
						}
						ref={triggerRef}
					/>
				</Box>
			)}
		>
			{(props) => (
				<>
					<DismissalButton
						{...props}
						flagProps={{
							title: formatMessage(messages.dismissFlagTitle),
							description: formatMessage(messages.dismissFlagDescription),
						}}
						onClick={() => {
							fireUIAnalytics(buttonClicked, 'notInterestedButton', eventAttributes);
							return onDismiss();
						}}
					/>
					<ReasonsButton
						{...props}
						onClick={() => {
							fireUIAnalytics(buttonClicked, 'whyAmISeeingThisButton', eventAttributes);
						}}
						modalProps={{
							title: formatMessage(messages.reasonsModalTitle),
							body: (
								<ContextualAnalyticsData sourceName="whyAmISeeingThis" sourceType={MODAL}>
									<MountEvent
										onMount={(analyticsEvent: AnalyticsEvent) => {
											fireScreenAnalytics(analyticsEvent, eventAttributes);
										}}
									/>
									{formatMessage(messages.reasonsModalExplanation, {
										strong: (chunks: ReactNode[]) => <Text weight="bold">{chunks}</Text>,
									})}
									<ul>
										<li>{formatMessage(messages.reasonsModalBulletOne)}</li>
										<li>{formatMessage(messages.reasonsModalBulletTwo)}</li>
									</ul>
									<Box xcss={linkStyles}>
										<Button
											appearance="link"
											spacing="none"
											href={DATA_PRIVACY_LINK}
											target="_blank"
											onClick={() => {
												fireUIAnalytics(linkClicked, 'dataPrivacyLink');
											}}
										>
											{formatMessage(messages.privacyPolicy)}
										</Button>
									</Box>
								</ContextualAnalyticsData>
							),
						}}
					/>
				</>
			)}
		</AdControlDropdownMenu>
	);
};

const linkStyles = xcss({
	marginTop: 'space.150',
});

export default AdControls;
